import { App } from 'vue';

interface I18nEditorConfig {
  enableEditMode?: boolean;
  autoLoadFromDB?: boolean;
}

declare global {
  interface Window {
    __I18N_EDIT_MODE__: boolean;
    __I18N_EDITOR__: {
      openModal: (key: string, locale: string, value: string) => void;
      closeModal: () => void;
    };
  }
}

let originalT: Function;
let isInitialized = false;
let i18nInstance: any;
let contextMenuHandler: Function | null = null;

/**
 * Create the i18n editor plugin
 */
export function createI18nEditorPlugin(i18n: any, config: I18nEditorConfig = {}) {
  return {
    install(app: App) {
      i18nInstance = i18n;

      // Initialize edit mode state
      window.__I18N_EDIT_MODE__ = config.enableEditMode ?? false;

      // Store original $t for reference but DON'T override it
      // We use smart context menu instead
      originalT = i18n.global.t.bind(i18n.global);

      // Make the override available on app instance (but don't use it)
      app.config.globalProperties.$t = i18n.global.t;

      isInitialized = true;
    },
  };
}

/**
 * Enable/Disable edit mode
 */
export function setI18nEditMode(enabled: boolean): void {
  window.__I18N_EDIT_MODE__ = enabled;

  // Trigger re-render of all components by updating locale
  if (i18nInstance) {
    const currentLocale = i18nInstance.global.locale.value;
    i18nInstance.global.locale.value = currentLocale;
  }
}

/**
 * Check if a string looks like a translation key (e.g. "meeting.title", "nav.menu.home")
 * Also accepts keys with spaces like "question.Allow File Attachment"
 * And keys with apostrophes like "question.Framework's Compliance"
 * And keys with special chars like "common.Show/Hide & Reorder Columns"
 * Used to detect when a key failed to resolve and is rendered as-is.
 */
function isLikelyTranslationKey(text: string): boolean {
  // Must contain at least one dot
  if (!text.includes(".")) return false;

  // Split by dot to get segments
  const segments = text.split(".");
  
  // First segment (before first dot) must be valid identifier
  if (segments[0].length === 0 || !/^[a-zA-Z0-9_]+$/.test(segments[0])) {
    return false;
  }

  // Rest of segments can contain spaces, apostrophes, slashes, ampersands, and words/numbers/underscores
  // e.g., "question.Allow File Attachment" or "form.Framework's Compliance" or "common.Show/Hide & Reorder Columns"
  for (let i = 1; i < segments.length; i++) {
    const seg = segments[i];
    if (seg.length === 0) return false;
    
    // Allow spaces, letters, numbers, underscores, hyphens, apostrophes, slashes, ampersands
    if (!/^[a-zA-Z0-9_\s\-'\/&]+$/.test(seg)) {
      return false;
    }
  }

  return true;
}

/**
 * Register global right-click listener - SMART VERSION
 * Works with ANY text, finds matching translation key automatically
 */
export function registerI18nContextMenu(
  onEdit: (key: string, locale: string, value: string) => void,
): void {
  if (!isInitialized) {
    return;
  }

  // Remove existing listener if any
  if (contextMenuHandler) {
    document.removeEventListener("contextmenu", contextMenuHandler as any);
  }

  contextMenuHandler = (event: MouseEvent) => {
    if (!window.__I18N_EDIT_MODE__) {
      return;
    }

    const target = event.target as HTMLElement;

    if (!i18nInstance) {
      return;
    }

    const locale = i18nInstance.global.locale.value;
    const messages = i18nInstance.global.getLocaleMessage(locale);

    console.log(
      "[i18nEditor] Right-click on:",
      target.tagName,
      target.textContent?.trim(),
    );

    // ─── Check for attribute-based translations ONLY FOR FORM INPUTS ───────────
    // For inputs, textareas, selects: check placeholder/title attributes FIRST
    const isFormInput = ["INPUT", "TEXTAREA", "SELECT"].includes(
      target.tagName,
    );
    if (isFormInput) {
      const attributesToCheck = [
        "placeholder",
        "title",
        "aria-label",
        "data-title",
        "alt",
      ];
      for (const attr of attributesToCheck) {
        const attrValue = target.getAttribute(attr);
        if (attrValue && attrValue.trim().length > 0) {
          console.log(
            `[i18nEditor] Found ${attr} attribute:`,
            `"${attrValue}"`,
          );

          // FIRST: Check if the attribute value IS ITSELF a translation key
          if (isLikelyTranslationKey(attrValue)) {
            console.log(
              `[i18nEditor] ✓ Attribute value is a translation key:`,
              attrValue,
            );
            const existingValue = getTranslationValue(messages, attrValue);
            event.preventDefault();
            onEdit(
              attrValue,
              locale,
              existingValue === attrValue ? "" : existingValue,
            );
            return;
          }

          // SECOND: Search for a key that matches the attribute value as translation
          let key = findTranslationKey(attrValue, messages);
          if (key) {
            console.log(
              `[i18nEditor] ✓ Found translation key from ${attr}:`,
              key,
            );
            event.preventDefault();
            onEdit(key, locale, attrValue);
            return;
          }
        }
      }
    }

    // ─── Get and clean text FIRST (before any strategy checks) ──────────────────
    let textContent = target.textContent?.trim();

    let searchElement: HTMLElement | null = target;
    let searchDepth = 0;
    while (
      (!textContent || textContent.length === 0) &&
      searchElement &&
      searchDepth < 10
    ) {
      searchElement = searchElement.parentElement;
      if (searchElement) {
        textContent = searchElement.textContent?.trim();
      }
      searchDepth++;
    }

    if (!textContent || textContent.length === 0) {
      console.log("[i18nEditor] No text content found");
      return;
    }

    // Clean up text (remove asterisks, extra whitespace, trailing punctuation)
    textContent = textContent
      .replace(/[\*✔✕×+]/g, "") // Remove special characters
      .replace(/\s+/g, " ") // Normalize whitespace
      .replace(/[:\.,;!?\-\(\)]+\s*$/g, "") // Remove trailing punctuation
      .trim();

    if (!textContent || textContent.length === 0) {
      console.log("[i18nEditor] Text content empty after cleaning");
      return;
    }

    console.log("[i18nEditor] Cleaned text:", `"${textContent}"`);

    // ─── Strategy 0: Clean text IS the key (untranslated / missing translation) ───
    // Check AFTER cleaning so we detect keys like "question.Regulator" properly
    if (isLikelyTranslationKey(textContent)) {
      console.log(
        "[i18nEditor] Strategy 0: Detected untranslated key format:",
        textContent,
      );
      const existingValue = getTranslationValue(messages, textContent);
      event.preventDefault();
      onEdit(
        textContent,
        locale,
        existingValue === textContent ? "" : existingValue,
      );
      return;
    }

    // ─── Strategy 1: data-i18n-key attribute on element or any ancestor ───────
    let element: HTMLElement | null = target;
    let depth = 0;
    while (element && depth < 10) {
      const i18nKey = element.getAttribute("data-i18n-key");
      if (i18nKey) {
        console.log("[i18nEditor] Strategy 1: Found data-i18n-key:", i18nKey);
        const foundValue = element.textContent?.trim() || i18nKey;
        event.preventDefault();
        onEdit(i18nKey, locale, foundValue);
        return;
      }
      element = element.parentElement;
      depth++;
    }

    // ─── Strategy 2: Context-aware matching (with DOM context clues) ──────────
    console.log("[i18nEditor] Strategy 2: Trying context-aware matching...");
    let key = findTranslationKeyOptimized(textContent, messages, target);
    if (key) {
      console.log("[i18nEditor] ✓ Strategy 2 SUCCESS:", key);
      event.preventDefault();
      onEdit(key, locale, textContent);
      return;
    } else {
      console.log("[i18nEditor] ✗ Strategy 2 failed");
    }

    // ─── Strategy 3: Extract words from mixed content ────────────────────────
    console.log("[i18nEditor] Strategy 3: Trying word extraction...");
    const wordsOnly = textContent.replace(/^\d+\s+/g, "").trim();
    if (wordsOnly && wordsOnly !== textContent) {
      key = findTranslationKeyOptimized(wordsOnly, messages, target);
      if (key) {
        console.log("[i18nEditor] ✓ Strategy 3 SUCCESS:", key);
        event.preventDefault();
        onEdit(key, locale, wordsOnly);
        return;
      } else {
        console.log("[i18nEditor] ✗ Strategy 3 failed");
      }
    }

    // ─── Strategy 4: Direct text match with special character handling ────────
    console.log("[i18nEditor] Strategy 4: Trying direct match with special chars...");
    key = findTranslationKeyWithSpecialChars(textContent, messages);
    if (key) {
      console.log("[i18nEditor] ✓ Strategy 4 SUCCESS:", key);
      event.preventDefault();
      onEdit(key, locale, textContent);
      return;
    } else {
      console.log("[i18nEditor] ✗ Strategy 4 failed");
    }

    // ─── Strategy 5: Partial word match ──────────────────────────────────────
    console.log("[i18nEditor] Strategy 5: Trying partial word match...");
    key = findTranslationKeyPartial(textContent, messages);
    if (key) {
      console.log("[i18nEditor] ✓ Strategy 5 SUCCESS:", key);
      const fullValue = getTranslationValue(messages, key);
      event.preventDefault();
      onEdit(key, locale, fullValue);
      return;
    } else {
      console.log("[i18nEditor] ✗ Strategy 5 failed");
    }

    // ─── Strategy 6: Normalized text match ───────────────────────────────────
    console.log("[i18nEditor] Strategy 6: Trying normalized match...");
    const normalizedText = textContent.toLowerCase();
    key = findTranslationKeyByNormalized(normalizedText, messages);
    if (key) {
      console.log("[i18nEditor] ✓ Strategy 6 SUCCESS:", key);
      const fullValue = getTranslationValue(messages, key);
      event.preventDefault();
      onEdit(key, locale, fullValue);
      return;
    } else {
      console.log("[i18nEditor] ✗ Strategy 6 failed - no key found");
    }

    // ─── FALLBACK: Check attributes for non-form elements (lower priority) ───
    // If all text-based strategies failed, try attributes for other elements
    if (!isFormInput) {
      const attributesToCheck = ["title", "aria-label", "data-title"];
      for (const attr of attributesToCheck) {
        const attrValue = target.getAttribute(attr);
        if (attrValue && attrValue.trim().length > 0) {
          console.log(
            `[i18nEditor] FALLBACK: Checking ${attr} attribute:`,
            `"${attrValue}"`,
          );

          if (isLikelyTranslationKey(attrValue)) {
            console.log(
              `[i18nEditor] ✓ FALLBACK: Attribute is a translation key:`,
              attrValue,
            );
            const existingValue = getTranslationValue(messages, attrValue);
            event.preventDefault();
            onEdit(
              attrValue,
              locale,
              existingValue === attrValue ? "" : existingValue,
            );
            return;
          }

          let key = findTranslationKey(attrValue, messages);
          if (key) {
            console.log(
              `[i18nEditor] ✓ FALLBACK: Found translation key from ${attr}:`,
              key,
            );
            event.preventDefault();
            onEdit(key, locale, attrValue);
            return;
          }
        }
      }
    }
  };

  document.addEventListener("contextmenu", contextMenuHandler as any, false);

  // Mark listener as registered
  (window as any).__I18N_EDITOR_LISTENER__ = true;
}

/**
 * Find translation key that matches the text
 * Searches through all translation keys recursively
 */
function findTranslationKey(
  text: string,
  messages: any,
  path: string = "",
): string | null {
  const normalizedText = text.trim().toLowerCase();

  const searchKey = (obj: any, currentPath: string = ""): string | null => {
    if (!obj || typeof obj !== "object") return null;

    for (const [key, value] of Object.entries(obj)) {
      const fullPath = currentPath ? `${currentPath}.${key}` : key;

      if (typeof value === "string") {
        if (value.trim().toLowerCase() === normalizedText) {
          return fullPath;
        }
      } else if (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value)
      ) {
        const found = searchKey(value, fullPath);
        if (found) return found;
      }
    }
    return null;
  };

  return searchKey(messages, path);
}

/**
 * Extract DOM context clues to help identify the correct translation key
 * Looks for parent label's 'for' attribute, form control names, modal/answer context
 * Enhanced to handle SPANs inside labels and labels without 'for' attributes
 */
function extractDOMContext(element: HTMLElement): string[] {
  const clues: string[] = [];

  // Strategy 1: Find parent label with 'for' attribute (HIGHEST PRIORITY)
  let parent: HTMLElement | null = element;
  let labelDepth = 0;
  let parentLabel: HTMLElement | null = null;

  // If element IS the label, use it directly
  if (element.tagName === "LABEL") {
    parentLabel = element;
  } else {
    // Otherwise traverse up to find parent label
    while (parent && labelDepth < 15) {
      if (parent.tagName === "LABEL") {
        parentLabel = parent;
        break;
      }
      parent = parent.parentElement;
      labelDepth++;
    }
  }

  // Extract the 'for' attribute if we found a label
  if (parentLabel) {
    const forAttr = parentLabel.getAttribute("for");
    if (forAttr) {
      clues.push(forAttr);
      // Split by common delimiters
      forAttr.split(/[_\-]/g).forEach((part) => {
        if (part && part !== "id" && part.length > 0) {
          clues.push(part.toLowerCase());
        }
      });
      console.log(
        "[i18nEditor] Found label with for attribute:",
        forAttr,
        "-> clues:",
        clues,
      );
      return [...new Set(clues)];
    }
    
    // If label found but NO 'for' attribute, try to find adjacent form controls
    console.log(
      "[i18nEditor] Found label without for attribute, searching for adjacent form controls...",
    );
    const siblings = parentLabel.parentElement?.children;
    if (siblings) {
      for (const sibling of Array.from(siblings)) {
        const controlName = (sibling as HTMLElement).getAttribute("name") || 
                           (sibling as HTMLElement).getAttribute("id");
        if (controlName && controlName !== element.getAttribute("id")) {
          clues.push(controlName);
          controlName.split(/[_\-]/g).forEach((part) => {
            if (part && part.length > 0) {
              clues.push(part.toLowerCase());
            }
          });
          console.log(
            "[i18nEditor] Found sibling form control:",
            controlName,
            "-> clues:",
            clues,
          );
          return [...new Set(clues)];
        }
      }
    }

    // Also check children of the label for form controls
    const labelFormControl = parentLabel.querySelector("input, select, textarea");
    if (labelFormControl) {
      const controlName = (labelFormControl as HTMLElement).getAttribute("name") || 
                         (labelFormControl as HTMLElement).getAttribute("id");
      if (controlName) {
        clues.push(controlName);
        controlName.split(/[_\-]/g).forEach((part) => {
          if (part && part.length > 0) {
            clues.push(part.toLowerCase());
          }
        });
        console.log(
          "[i18nEditor] Found nested form control in label:",
          controlName,
          "-> clues:",
          clues,
        );
        return [...new Set(clues)];
      }
    }
  }

  // Strategy 2: If no label found, look for nearby form control attributes (name, id)
  parent = element;
  let controlDepth = 0;
  while (parent && controlDepth < 10) {
    // Check the element itself
    let controlName =
      element.getAttribute("name") || element.getAttribute("id");
    if (!controlName && parent.querySelector) {
      const formControl = parent.querySelector("input, select, textarea");
      if (formControl) {
        controlName =
          formControl.getAttribute("name") || formControl.getAttribute("id");
      }
    }

    if (controlName) {
      clues.push(controlName);
      controlName.split(/[_\-]/g).forEach((part) => {
        if (part && part.length > 0) {
          clues.push(part.toLowerCase());
        }
      });
      break;
    }

    parent = parent.parentElement;
    controlDepth++;
  }

  // Strategy 3: Check for modal/answer context in class names
  parent = element;
  let contextDepth = 0;
  while (parent && contextDepth < 10) {
    const classList = parent.className || "";

    if (
      classList.includes("modal-overlay") ||
      classList.includes("modal-content")
    ) {
      clues.push("modal");
      clues.push("risk");
    }

    if (
      classList.includes("answer-item") ||
      classList.includes("answers-section")
    ) {
      clues.push("answer");
    }

    parent = parent.parentElement;
    contextDepth++;
  }

  return [...new Set(clues)]; // Remove duplicates
}

/**
 * Find translation key with context-aware scoring
 * Prioritizes keys that match DOM context clues (label 'for' attribute, form field names, etc)
 */
function findTranslationKeyOptimized(
  text: string,
  messages: any,
  sourceElement: HTMLElement,
): string | null {
  const normalizedText = text.trim().toLowerCase();
  const contextClues = extractDOMContext(sourceElement);

  console.log(
    "[i18nEditor] Searching for text:",
    `"${text}"`,
    "with context clues:",
    contextClues,
  );

  const matches: Array<{
    key: string;
    score: number;
    contextMatches: number;
  }> = [];

  const searchKey = (obj: any, path: string = ""): void => {
    if (!obj || typeof obj !== "object") return;

    for (const [key, value] of Object.entries(obj)) {
      const fullPath = path ? `${path}.${key}` : key;

      if (typeof value === "string") {
        if (value.trim().toLowerCase() === normalizedText) {
          let score = 0;
          let contextMatches = 0;
          const fullPathLower = fullPath.toLowerCase();

          // High priority: Count how many context clues match the key
          for (const clue of contextClues) {
            if (fullPathLower.includes(clue.toLowerCase())) {
              score += 50; // Very high priority for context matches
              contextMatches++;
            }
          }

          // Bonus for key specificity (more dots = more specific)
          const dotCount = (fullPath.match(/\./g)?.length || 0);
          score += dotCount * 10;

          // Baseline score: if no context clues at all, or no matches found, still give points
          if (contextClues.length === 0) {
            score += 5;
          } else if (contextMatches === 0 && contextClues.length > 0) {
            // No context matched, but we still found text match
            // Give a smaller baseline to still consider it
            score += 2;
          }

          matches.push({ key: fullPath, score, contextMatches });
          console.log(
            `[i18nEditor]   Found match: "${fullPath}" | contextMatches: ${contextMatches} | dots: ${dotCount} | score: ${score}`,
          );
        }
      } else if (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value)
      ) {
        searchKey(value, fullPath);
      }
    }
  };

  searchKey(messages);

  if (matches.length === 0) {
    console.log("[i18nEditor] No matches found for text:", `"${text}"`);
    return null;
  }

  // If only 1 match, return it immediately
  if (matches.length === 1) {
    console.log(
      `[i18nEditor] ✓ Only 1 match found: "${matches[0].key}"`,
    );
    return matches[0].key;
  }

  // Primary sort: by context matches (how many clues matched)
  // Secondary sort: by score (higher is better)
  // Tertiary sort: by key specificity (more specific first)
  matches.sort((a, b) => {
    if (b.contextMatches !== a.contextMatches) {
      return b.contextMatches - a.contextMatches;
    }
    if (b.score !== a.score) {
      return b.score - a.score;
    }
    return (
      (b.key.match(/\./g)?.length || 0) - (a.key.match(/\./g)?.length || 0)
    );
  });

  const selectedKey = matches[0].key;
  console.log(
    `[i18nEditor] ✓ Selected key: "${selectedKey}" (contextMatches: ${matches[0].contextMatches}, score: ${matches[0].score})`,
  );
  console.log(
    "[i18nEditor] All matches ranked:",
    matches.slice(0, 5).map((m) => `${m.key} (ctx:${m.contextMatches}, score:${m.score})`),
  );

  return selectedKey;
}

/**
 * Find translation key by direct text match, preserving special characters
 * Handles text like "Show/Hide & Reorder Columns" better than normalization
 */
function findTranslationKeyWithSpecialChars(
  text: string,
  messages: any,
): string | null {
  const searchText = text.trim().toLowerCase();

  const searchKey = (obj: any, path: string = ""): string | null => {
    if (!obj || typeof obj !== "object") return null;

    for (const [key, value] of Object.entries(obj)) {
      const fullPath = path ? `${path}.${key}` : key;

      if (typeof value === "string") {
        const translatedText = value.trim().toLowerCase();
        
        // Try exact match first
        if (translatedText === searchText) {
          return fullPath;
        }

        // Try match with normalized spaces only (preserve special chars)
        const normalizedSearch = searchText.replace(/\s+/g, " ");
        const normalizedValue = translatedText.replace(/\s+/g, " ");
        if (normalizedValue === normalizedSearch) {
          return fullPath;
        }
      } else if (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value)
      ) {
        const found = searchKey(value, fullPath);
        if (found) return found;
      }
    }
    return null;
  };

  return searchKey(messages);
}

/**
 * Find translation key by partial text match (for dropdowns with extra text)
 */
function findTranslationKeyPartial(text: string, messages: any): string | null {
  const searchWords = text
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w.length > 0);

  const searchKey = (obj: any, path: string = ""): string | null => {
    if (!obj || typeof obj !== "object") return null;

    for (const [key, value] of Object.entries(obj)) {
      const fullPath = path ? `${path}.${key}` : key;

      if (typeof value === "string") {
        const valueWords = value
          .toLowerCase()
          .split(/\s+/)
          .filter((w) => w.length > 0);

        for (const word of searchWords) {
          for (const valueWord of valueWords) {
            if (
              valueWord === word ||
              valueWord.includes(word) ||
              word.includes(valueWord)
            ) {
              return fullPath;
            }
          }
        }
      } else if (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value)
      ) {
        const found = searchKey(value, fullPath);
        if (found) return found;
      }
    }
    return null;
  };

  return searchKey(messages);
}

/**
 * Find translation key by normalized text
 */
function findTranslationKeyByNormalized(
  text: string,
  messages: any,
): string | null {
  const normalizedText = text
    .trim()
    .toLowerCase()
    .replace(/[^\w\s]/g, "");

  const searchKey = (obj: any, path: string = ""): string | null => {
    if (!obj || typeof obj !== "object") return null;

    for (const [key, value] of Object.entries(obj)) {
      const fullPath = path ? `${path}.${key}` : key;

      if (typeof value === "string") {
        const normalizedValue = value.toLowerCase().replace(/[^\w\s]/g, "");
        if (normalizedValue === normalizedText) {
          return fullPath;
        }
      } else if (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value)
      ) {
        const found = searchKey(value, fullPath);
        if (found) return found;
      }
    }
    return null;
  };

  return searchKey(messages);
}

/**
 * Get translation value from messages by key
 */
function getTranslationValue(messages: any, key: string): string {
  const keys = key.split(".");
  let current = messages;

  for (const k of keys) {
    if (current && typeof current === "object" && k in current) {
      current = current[k];
    } else {
      return key; // key not found — returns the key itself as sentinel
    }
  }

  return typeof current === "string" ? current : key;
}

/**
 * Unregister context menu listener (cleanup)
 */
export function unregisterI18nContextMenu(): void {
  if (contextMenuHandler) {
    document.removeEventListener('contextmenu', contextMenuHandler as any);
    contextMenuHandler = null;
  }
}

/**
 * Utility: Escape HTML to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Get i18n instance (for external use)
 */
export function getI18n() {
  return i18nInstance;
}